<template>
    <div
        class="event-row-container"
        :class="{
            'is-locked': event.isLocked,
            'is-highlighted': isHighlighted,
            'is-grid-view-overridden': isGridViewOverridden,
        }"
        :id="`event-${event.id}`"
    >
        <ExtendedEventCard
            v-if="shouldUseExtendedEventCard"
            :event="event"
            :expandedEvent="expandedEvent"
            :isForceVerticalAligned="isForceVerticalAligned"
            :market="primaryMarket"
            :oddValueDynamics="oddValueDynamics"
            :selectedOdds="selectedOdds"
            :isPrimaryMarketSelected="isPrimaryMarketSelected"
            :isPinShown="isPinnable"
            :showTitle="showTitle"
            class="event-row-container__event"
            @click.native="handleClick($event)"
            @oddClick="onOddClick"
            @pinClick="onPinClick"
            :eventCardSource="eventCardSource"
            :bettorNumber="bettorNumber"
            :socialProofDesignVariant="socialProofDesignVariant"
        />
        <EventCard
            v-else
            :event="event"
            :expandedEvent="expandedEvent"
            :isForceVerticalAligned="isForceVerticalAligned"
            :market="primaryMarket"
            :oddValueDynamics="oddValueDynamics"
            :selectedOdds="selectedOdds"
            :isPrimaryMarketSelected="isPrimaryMarketSelected"
            :isPinShown="isPinnable"
            :showTitle="showTitle"
            class="event-row-container__event"
            @click.native="handleClick($event)"
            @oddClick="onOddClick"
            @pinClick="onPinClick"
            :eventCardSource="eventCardSource"
            :bettorNumber="bettorNumber"
            :socialProofDesignVariant="socialProofDesignVariant"
        />
        <MatchFooter
            v-if="event.matchFooter"
            :content="event.matchFooter"
        />
    </div>
</template>

<script>
    import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
    import { SelectionSourceScreen } from '@superbet-group/web.types';
    import {
        mapGetters,
        mapActions,
    } from 'vuex';

    import { MarketAttribute } from '@core/constants';
    import MatchFooter from '@modules/eventGrid/components/MatchFooter';
    import { getFilledMarketOdds } from '@modules/eventGrid/fillingOddsUtilities';
    import EventCard from '@modules/shared/event/EventCard.vue';
    import ExtendedEventCard from '@modules/shared/event/ExtendedEventCard.vue';
    import { getEventSlugFromEvent } from '@app/modules/eventDetailsPage/utils';
    import { PeerUrgencyExperimentVariant } from '@core/models/user/flags';
    import { MAIN_MARKET_ID } from '@store/modules/ui/sportOffer/marketFilters/constants';

    const ANIMATION_DURATION = 2000; // ms

    export default {
        name: 'EventRow',
        emits: [
            'eventClick',
            'expand',
            'collapse',
            'rowHeightChanged',
            'removeHighlight',
            'pinClick',
        ],
        props: {
            event: {
                type: Object,
                required: true,
            },
            typeId: {
                type: [String, Number],
                default: null,
            },
            isPinned: {
                type: Boolean,
                required: false,
                default: false,
            },
            selectedOdds: {
                type: Set,
                required: false,
                default: () => new Set(),
            },
            primaryMarketAttribute: {
                type: String,
                default: MarketAttribute.main,
            },
            selectedMarketId: {
                type: Number,
                required: false,
                default: undefined,
            },
            isPinnable: {
                type: Boolean,
                default: true,
            },
            isHighlighted: {
                type: Boolean,
                required: false,
            },
            isForceVerticalAligned: {
                type: Boolean,
                required: false,
                default: false,
            },
            oddValueDynamics: {
                type: Object,
                required: false,
                default: undefined,
            },
            displayMarketCount: {
                type: Boolean,
                default: true,
            },
            isCodeShown: {
                type: Boolean,
                required: false,
                default: false,
            },
            isGridViewActive: {
                type: Boolean,
                required: true,
            },
            isGridViewOverridden: {
                type: Boolean,
                required: false,
            },
            gridViewMarketOddTypes: {
                type: Array,
                required: false,
                default: null,
            },
            isPrematchMarketForced: {
                type: Boolean,
                required: true,
            },
            getPrimaryMarket: {
                type: Function,
                required: true,
            },
            showTitle: {
                type: Boolean,
                default: false,
            },
            sourceScreen: {
                type: Number, // as PropType<SelectionSourceScreen>,
                required: false,
                default: SelectionSourceScreen.Unknown,
            },
            eventCardSource: {
                type: String, // as PropType<EventCardSource>,
                required: true,
            },
            shouldShowExtraMarket: {
                type: Boolean,
                default: false,
            },
            bettorNumber: {
                type: Number,
                required: false,
                default: -1,
            },
            socialProofDesignVariant: {
                type: String,
                required: false,
                default: PeerUrgencyExperimentVariant.None,
            },
        },
        computed: {
            ...mapGetters('data/authentication', [
                'user',
            ]),
            ...mapGetters('ui/sportOffer', [
                'expandedEvent',
            ]),
            ...mapGetters('data/betBuilder', [
                'isBetBuilderAvailable',
            ]),
            additionalMarketCount() {
                return Math.max(0, this.event.marketCount - 1);
            },
            primaryMarket() {
                const primaryMarket = this.getPrimaryMarket(
                    this.selectedMarketId,
                    this.event,
                    this.primaryMarketAttribute,
                    this.isPrematchMarketForced,
                );

                if (!primaryMarket) {
                    return null;
                }

                // Since we might dealing with a non-main market, we might have more than
                // 3 odds, which is what the spec, offer, and design all expect.
                // In this case, let's artificially limit it without mutating the original.
                // Both cases must be covered, grid view or not.
                if (!this.isGridViewActive) {
                    return {
                        id: primaryMarket.id,
                        name: primaryMarket.name,
                        isLocked: primaryMarket.isLocked,
                        odds: primaryMarket.odds.slice(0, 3),
                    };
                }

                const filledOdds = getFilledMarketOdds(
                    this.event,
                    primaryMarket,
                    this.gridViewMarketOddTypes,
                ).slice(0, 3);

                return {
                    id: primaryMarket.id,
                    name: primaryMarket.name,
                    isLocked: primaryMarket.isLocked,
                    odds: filledOdds,
                };
            },
            hasSelectionInCollapsedMarkets() {
                return this.selectedOdds.size > 0
                    && this.primaryMarket
                    && !this.primaryMarket.odds.find((x) => this.selectedOdds.has(x.uuid));
            },
            isPrimaryMarketSelected() {
                if (!this.selectedMarketId) {
                    return true;
                }
                return this.selectedMarketId === MAIN_MARKET_ID;
            },
            shouldUseExtendedEventCard() {
                return this.shouldShowExtraMarket && this.additionalMarketCount > 0;
            },
        },
        watch: {
            isHighlighted(isHighlighted) {
                if (isHighlighted) {
                    if (this.timeout) {
                        clearTimeout();
                    }
                    this.timeout = setTimeout(() => {
                        this.$emit('removeHighlight');
                    }, ANIMATION_DURATION);
                }
            },
        },
        methods: {
            ...mapActions('data/betBuilder', {
                clearBetBuilder: 'clear',
            }),
            ...mapActions('ui/sportOffer/betSlip', [
                'toggleSelection',
            ]),
            /* this logic is used to avoid using stopPropagation on click because of reasons explained here
                https://css-tricks.com/dangers-stopping-event-propagation/
            */
            handleClick(nativeEvent) {
                const target = nativeEvent.target;
                let clickedOnEvent = false;
                if (target === this.$el) {
                    clickedOnEvent = true;
                } else {
                    /*
                    class 'actionable' is a convention to put on html elements with their own click handlers.
                    These elements shouldn't activate their parent click handler
                */
                    clickedOnEvent = target.className.indexOf('actionable') === -1 && target.closest('.actionable') === null;
                }
                if (clickedOnEvent) {
                    const offsetTop = this.$el.getBoundingClientRect().y;

                    this.$emit('eventClick', {
                        offsetTop,
                        eventSlug: getEventSlugFromEvent(this.event),
                        eventId: this.event.id,
                        typeId: this.typeId,
                    });
                }
            },
            onOddClick(odd) {
                if (this.event.isLocked || odd.isLocked) {
                    return;
                }

                const { id: oddId, specialBetValue, uuid } = odd;
                this.toggleSelection({
                    eventId: this.event.id,
                    oddId,
                    uuid,
                    specialBetValue,
                    sourceScreen: this.sourceScreen,
                });
            },
            onPinClick(eventId) {
                AnalyticsSDK.getInstance().log('Match_Pin', { status: this.isPinned ? 'unpin' : 'pin' });
                this.$emit('pinClick', eventId);
            },
            emitHeightChange() {
                this.$emit('rowHeightChanged', this.$el.clientHeight);
            },
            async emitUserChoice(event) {
                const offsetTop = this.$el.getBoundingClientRect().y;
                this.$emit(event, {
                    offsetTop,
                });
                await this.$nextTick();
                this.emitHeightChange();
            },
            emitMarketGroupChange() {
                this.emitUserChoice('marketGroupChanged');
            },
        },
        beforeDestroy() {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
        },
        components: {
            EventCard,
            ExtendedEventCard,
            MatchFooter,
        },
    };
</script>

<style lang="scss" scoped>
    @import '~@superbet-group/web.lib.global-styles/colors';

    .event-row-container {
        display: flex;
        flex-direction: column;

        &.is-expanded {
            gap: 8px;
            background-color: var(--body-background-color);
        }

        &__event {
            cursor: pointer;
        }
    }

</style>
